import { createFileRoute, Outlet, redirect, Link, useLocation } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { AppTopbar } from "@/components/app-topbar";
import { TourProvider } from "@/components/tour/tour-provider";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { ImpersonationBanner } from "@/components/impersonation-banner";
import { MaintenanceBanner } from "@/components/maintenance-banner";
import { DunningBanner, isPaymentBlocked, type DunningInfo } from "@/components/dunning-banner";
import { Button } from "@/components/ui/button";
import { CreditCard, Check } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { createCheckoutSession } from "@/lib/billing.functions";
import { PLANS, PLAN_ORDER, type PlanKey, type BillingInterval } from "@/lib/billing-plans";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

type BillingState = DunningInfo & {
  plan: string | null;
  stripe_subscription_id: string | null;
  is_admin_comped: boolean | null;
  is_platform_admin: boolean;
};

export const Route = createFileRoute("/_authenticated")({
  beforeLoad: async ({ location }) => {
    if (typeof window === "undefined") return;
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
      throw redirect({ to: "/login", search: { redirect: location.href } as never });
    }
    // First-run onboarding gate. Skip the check on /onboarding itself.
    if (location.pathname !== "/onboarding") {
      try {
        const { data: profile } = await supabase
          .from("profiles")
          .select("onboarding_completed_at")
          .eq("id", data.session.user.id)
          .maybeSingle();
        if (profile && !profile.onboarding_completed_at) {
          throw redirect({ to: "/onboarding" });
        }
      } catch (err) {
        // Re-throw redirects; swallow lookup failures so we don't block login on a transient DB blip.
        if (err && typeof err === "object" && "to" in (err as any)) throw err;
      }
    }
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isOnboarding = location.pathname === "/onboarding";
  const [billing, setBilling] = useState<BillingState | null>(null);
  const [billingLoaded, setBillingLoaded] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate({ to: "/login" });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: mems } = await supabase
          .from("account_members")
          .select("account_id")
          .eq("user_id", user.id);
        const ids = (mems ?? []).map((m) => m.account_id).filter(Boolean);
        if (ids.length === 0 || cancelled) return;
        const [{ data: accs }, { data: profile }] = await Promise.all([
          supabase
            .from("accounts")
            .select("id,plan,plan_status,dunning_state,dunning_started_at,stripe_subscription_id,is_admin_comped,created_at")
            .in("id", ids),
          supabase
            .from("profiles")
            .select("is_platform_admin")
            .eq("id", user.id)
            .maybeSingle(),
        ]);
        // Prefer an account with a Stripe subscription, then admin-comped,
        // then the most recently created. Avoids picking the orphan "trial"
        // placeholder when a real subscription exists on another account.
        const sorted = (accs ?? []).slice().sort((a, b) => {
          const aHas = a.stripe_subscription_id ? 1 : 0;
          const bHas = b.stripe_subscription_id ? 1 : 0;
          if (aHas !== bHas) return bHas - aHas;
          const aComp = a.is_admin_comped ? 1 : 0;
          const bComp = b.is_admin_comped ? 1 : 0;
          if (aComp !== bComp) return bComp - aComp;
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });
        const acc = sorted[0] ?? null;
        if (!cancelled) {
          setBilling(acc ? ({ ...acc, is_platform_admin: !!profile?.is_platform_admin } as BillingState) : null);
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(err instanceof Error ? err.message : "Could not verify billing status");
          setBilling(null);
        }
      } finally {
        if (!cancelled) setBillingLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const adminBypass = !!billing?.is_admin_comped || !!billing?.is_platform_admin;
  const paymentBlocked = adminBypass ? false : isPaymentBlocked(billing);
  // Needs to go through Stripe Checkout: no subscription on file yet.
  // Only gate AFTER the billing check completes, render the app optimistically
  // during the initial check so users don't see a blocking "checking billing"
  // card on every navigation.
  const needsCheckout =
    billingLoaded &&
    !!billing &&
    !paymentBlocked &&
    !adminBypass &&
    !billing.stripe_subscription_id;

  if (isOnboarding) {
    // Full-bleed onboarding, no sidebar, no topbar, no billing gates (the
    // user just paid in Stripe Checkout; we don't want to bounce them).
    return (
      <div className="min-h-screen w-full bg-muted/20">
        <Outlet />
      </div>
    );
  }

  return (
    <SidebarProvider>
      <TourProvider>
        <div className="flex min-h-screen w-full">
          <AppSidebar />
          <SidebarInset>
            <MaintenanceBanner />
            <ImpersonationBanner />
            <DunningBanner info={adminBypass ? null : billing} />
            <AppTopbar />
            <main className="flex-1 p-6">
              {paymentBlocked ? (
                <PaymentRequiredGate />
              ) : needsCheckout ? (
                <CheckoutRequiredGate />
              ) : (
                <Outlet />
              )}
            </main>
          </SidebarInset>
        </div>
      </TourProvider>
    </SidebarProvider>
  );
}


function PaymentRequiredGate() {
  return (
    <div className="mx-auto flex max-w-xl flex-col items-center justify-center gap-4 rounded-xl border border-border bg-card p-10 text-center">
      <div className="rounded-full bg-destructive/10 p-3 text-destructive">
        <CreditCard className="h-6 w-6" />
      </div>
      <h1 className="text-xl font-semibold">Your account is suspended</h1>
      <p className="text-sm text-muted-foreground">
        We weren't able to charge your payment method. Update it from the Customer Portal to
        reactivate your account. Imports, AI calls, and destructive changes are paused until
        payment is restored.
      </p>
      <Button asChild>
        <Link to="/settings/billing">
          <CreditCard className="mr-2 h-4 w-4" />
          Update payment method
        </Link>
      </Button>
    </div>
  );
}

function CheckoutRequiredGate() {
  const checkout = useServerFn(createCheckoutSession);
  const [plan, setPlan] = useState<PlanKey>("pro");
  const [interval, setInterval] = useState<BillingInterval>("monthly");
  const [submitting, setSubmitting] = useState(false);

  const start = async () => {
    setSubmitting(true);
    try {
      const res = await checkout({
        data: { plan, interval, origin: window.location.origin },
      });
      if (res?.url) window.location.href = res.url;
      else throw new Error("No checkout URL returned");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not start checkout");
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6 rounded-xl border border-border bg-card p-8">
      <div className="space-y-2 text-center">
        <h1 className="text-2xl font-semibold">Choose a plan to continue</h1>
        <p className="text-sm text-muted-foreground">
          Your account is created, but you haven't started a subscription yet. Pick a plan, we
          won't charge your card during the 14-day trial.
        </p>
      </div>

      <div className="grid grid-cols-2 rounded-lg border border-border bg-muted/30 p-1 text-sm">
        <button
          type="button"
          onClick={() => setInterval("monthly")}
          className={cn(
            "rounded-md py-1.5",
            interval === "monthly" ? "bg-background shadow-sm" : "text-muted-foreground",
          )}
        >
          Monthly
        </button>
        <button
          type="button"
          onClick={() => setInterval("annual")}
          className={cn(
            "rounded-md py-1.5",
            interval === "annual" ? "bg-background shadow-sm" : "text-muted-foreground",
          )}
        >
          Annual <span className="ml-1 text-xs text-emerald-500">save ~17%</span>
        </button>
      </div>

      <div className="space-y-2">
        {PLAN_ORDER.map((key) => {
          const def = PLANS[key];
          const price = def.display[interval];
          const selected = plan === key;
          return (
            <button
              type="button"
              key={key}
              onClick={() => setPlan(key)}
              className={cn(
                "w-full rounded-lg border p-3 text-left transition",
                selected
                  ? "border-primary bg-primary/5 ring-1 ring-primary"
                  : "border-border hover:border-primary/50",
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="font-medium">{def.name}</div>
                  {def.popular && (
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                      MOST POPULAR
                    </span>
                  )}
                </div>
                <div className="text-sm font-semibold">
                  ${price.amount.toLocaleString()}
                  <span className="text-xs font-normal text-muted-foreground">{price.suffix}</span>
                </div>
              </div>
              <div className="mt-1 text-xs text-muted-foreground">{def.tagline}</div>
              {selected && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-primary">
                  <Check className="h-3 w-3" /> 14-day free trial included
                </div>
              )}
            </button>
          );
        })}
      </div>

      <Button onClick={start} disabled={submitting} className="w-full">
        {submitting ? "Starting checkout…" : "Continue to checkout"}
      </Button>
      <p className="text-center text-xs text-muted-foreground">
        You won't be charged until after your 14-day trial. Cancel anytime from Settings → Billing.
      </p>
    </div>
  );
}

